import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MeetingService } from '../../../../../services/meeting.service';
import { UserService } from '../../../../../services/user.service';
import { Meeting, MeetingState } from '../../../../../models/meeting';
import { injectFocusTarget } from '../../../../../helpers/focus-target';
import {
  CounselorMeetingDetailsDialogComponent,
  CounselorMeetingDetailsDialogResult,
} from './counselor-meeting-details-dialog/counselor-meeting-details-dialog';
import {
  CounselorMeetingsCreate,
  CounselorMeetingsCreateResult,
} from './counselor-meetings-create/counselor-meetings-create';

/**
 * The states this table can show. 'pending' and 'expired' are absent: both are
 * still-pending rows, which the Pending tab owns and loadBookings() drops.
 */
export type BookingFilter = 'all' | MeetingState;

@Component({
  selector: 'app-counselor-meetings-bookings',
  imports: [DatePipe, FormsModule, MatFormFieldModule, MatSelectModule],
  templateUrl: './counselor-meetings-bookings.html',
  styleUrl: './counselor-meetings-bookings.css',
})
export class CounselorMeetingsBookings implements OnInit {
  readonly columns: { key: string; label: string }[] = [
    { key: 'date', label: 'When' },
    { key: 'name', label: 'Student Name' },
    { key: 'grade', label: 'Grade' },
    { key: 'topic', label: 'Topic' },
    { key: 'description', label: 'Description' },
    { key: 'state', label: 'Status' },
  ];

  readonly filters: { value: BookingFilter; label: string }[] = [
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'previous', label: 'Previous' },
    { value: 'canceled', label: 'Canceled' },
    { value: 'denied', label: 'Denied' },
    { value: 'all', label: 'All' },
  ];

  bookings: Meeting[] = [];

  visibleBookings: Meeting[] = [];

  private filter: BookingFilter = 'upcoming';

  private meetingService = inject(MeetingService);
  private userService = inject(UserService);
  private cdf = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);
  /**
   * Opens the meeting a notification is pointing at. A meeting still awaiting a
   * decision never reaches this list, so the Pending tab handles its own.
   */
  private focusTarget = injectFocusTarget<Meeting>({
    param: 'meetingId',
    items: () => this.bookings,
    onFocus: (meeting) => {
      this.statusFilter = meeting.state;
      this.cdf.detectChanges();
      this.openDetails(meeting);
    },
  });

  ngOnInit(): void {
    this.loadBookings();

    this.meetingService.meetingsChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadBookings());
  }

  /** Whether this booking has a proposal awaiting THIS counselor's own
   *  response. Drives the small red "needs attention" dot on its row. */
  needsAttention(meeting: Meeting): boolean {
    return meeting.awaitingResponseUserId === this.userService.currentUser?.id;
  }

  get statusFilter(): BookingFilter {
    return this.filter;
  }

  set statusFilter(value: BookingFilter) {
    this.filter = value;
    this.applyView();
  }

  /**
   * Filters on the stored status, not the derived state, so lapsed requests drop
   * out alongside those still awaiting a decision.
   * Students see lapsed requests under their own Previous filter.
   */
  private loadBookings(): void {
    this.meetingService.getMeetings().subscribe({
      next: (meetings) => {
        this.bookings = meetings.filter((m) => m.status !== 'pending');
        this.applyView();
        this.cdf.detectChanges();
        this.focusTarget.check();
      },
    });
  }

  /** Recomputed on load and on filter change — not on every CD pass. */
  private applyView(): void {
    const now = Date.now();

    const filtered =
      this.filter === 'all'
        ? [...this.bookings]
        : this.bookings.filter((m) => m.state === this.filter);

    this.visibleBookings = filtered.sort(CounselorMeetingsBookings.byRelevance(now));
  }

  /**
   * Meetings still ahead run soonest-first, past ones most-recent-first,
   * and anything ahead outranks anything past — so 'Upcoming' counts down toward
   * the next meeting while 'Previous' counts back from the last one.
   */
  private static byRelevance(now: number): (a: Meeting, b: Meeting) => number {
    return (a, b) => {
      const aTime = a.start?.getTime() ?? 0;
      const bTime = b.start?.getTime() ?? 0;
      const aAhead = aTime >= now;
      const bAhead = bTime >= now;
      if (aAhead !== bAhead) return aAhead ? -1 : 1;
      return aAhead ? aTime - bTime : bTime - aTime;
    };
  }

  /** Empty-state copy, phrased for whichever filter is active. */
  get emptyMessage(): string {
    switch (this.filter) {
      case 'upcoming':
        return 'You have no upcoming meetings';
      case 'previous':
        return 'You have no previous meetings';
      case 'canceled':
        return 'No meetings have been canceled';
      case 'denied':
        return 'You have not denied any meeting requests';
      default:
        return 'You have no meetings yet';
    }
  }

  /** Books a meeting with a student in the counselor's network. */
  createMeeting(): void {
    this.dialog
      .open(CounselorMeetingsCreate, {
        width: '640px',
        maxWidth: '96vw',
      })
      .afterClosed()
      .subscribe((result?: CounselorMeetingsCreateResult) => {
        if (result?.action === 'created') this.loadBookings();
      });
  }

  openDetails(meeting: Meeting): void {
    this.dialog
      .open(CounselorMeetingDetailsDialogComponent, {
        data: { meeting },
        width: '840px',
        maxWidth: '96vw',
      })
      .afterClosed()
      .subscribe((result?: CounselorMeetingDetailsDialogResult) => {
        if (result?.action === 'canceled' || result?.action === 'proposed') this.loadBookings();
      });
  }
}
